import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { api } from '../api/client';
import {
  AddGameEventInput,
  AddTxInput,
  ApiError,
  BootstrapData,
  Category,
  GameEvent,
  Goal,
  Operation,
  PutReserveInput,
  ReserveStateRow,
  SweepReserveInput,
  UpdateCategoryInput,
  UpdateGoalInput,
  UpdateTxInput,
} from '../api/types';
import { newClientId } from '../utils/ids';
import {
  drainQueue,
  enqueue,
  listQueue,
  QueueAction,
  QueueEntry,
  resetFailedAttempts,
} from '../storage/queue';
import { getSnapshot, putSnapshot } from '../storage/snapshot';
import { createRefreshScheduler } from './refresh-scheduler';

type LoadStatus = 'loading' | 'ready' | 'error';
type SyncStatus = 'online' | 'offline' | 'syncing';

interface ContextValue {
  status: LoadStatus;
  /** Сырое серверное состояние (или последний snapshot из IDB). */
  data: BootstrapData | null;
  /** Состояние с применёнными pending-мутациями из очереди — для рендера. */
  viewData: BootstrapData | null;
  error: string | null;

  sync: SyncStatus;
  queue: QueueEntry[];
  /** Сколько записей доехали до лимита попыток. */
  failedCount: number;
  /** Сессия истекла: очередь сохранена, нужен re-login (баннер в App). */
  needsLogin: boolean;

  refresh: () => Promise<void>;
  /** Возвращает id созданной операции — по нему считается детерминированная награда. */
  addTx: (input: Omit<AddTxInput, 'id'>) => Promise<MutationResult & { id: string }>;
  updateTx: (input: UpdateTxInput) => Promise<MutationResult>;
  deleteTx: (id: string) => Promise<MutationResult>;
  updateGoal: (input: UpdateGoalInput) => Promise<MutationResult>;
  updateCategory: (input: UpdateCategoryInput) => Promise<MutationResult>;
  /** Игровое событие через очередь: офлайн сундук/сезон доедут при синке.
   *  existing=true — сервер уже имел такое событие (singleton/повтор). */
  addGameEvent: (input: AddGameEventInput) => Promise<MutationResult & { existing?: boolean }>;
  /** Судьба резерва (released/снять) — общая для всех устройств, через очередь. */
  putReserve: (input: PutReserveInput) => Promise<MutationResult>;
  /** Смещение резерва в цель: транзакционно на сервере, идемпотентно по id. */
  sweepReserve: (input: SweepReserveInput) => Promise<MutationResult>;
  drainNow: () => Promise<void>;
  retryFailed: () => Promise<void>;
}

export type MutationResult = { synced: boolean };

const BudgetCtx = createContext<ContextValue | null>(null);

const DRAIN_INTERVAL_MS = 30_000;

/**
 * Плановый опрос сервера. Было 60 с — до 1440 полных bootstrap'ов в сутки с
 * устройства. С условным запросом (ETag → 304) опрос стоит одно чтение строки,
 * поэтому частота перестала быть критичной; 180 с — компромисс между свежестью
 * данных партнёра и шумом (план 3b).
 */
const POLL_INTERVAL_MS = 180_000;

/** Минимальный интервал между сверками с сервером после слива очереди. */
const REFRESH_MIN_INTERVAL_MS = 10_000;

export function BudgetProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<LoadStatus>('loading');
  const [data, setData] = useState<BootstrapData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sync, setSync] = useState<SyncStatus>(
    typeof navigator !== 'undefined' && !navigator.onLine ? 'offline' : 'online',
  );
  const [queue, setQueue] = useState<QueueEntry[]>([]);
  const [needsLogin, setNeedsLogin] = useState(false);
  const inflight = useRef<Promise<void> | null>(null);

  // refs для стабильных коллбэков — иначе useCallback с пустыми deps ловит stale closure
  const dataRef = useRef<BootstrapData | null>(null);
  const syncRef = useRef<SyncStatus>(sync);
  useEffect(() => { dataRef.current = data; }, [data]);
  useEffect(() => { syncRef.current = sync; }, [sync]);

  // ---- queue snapshot ----
  const refreshQueue = useCallback(async () => {
    const all = await listQueue();
    setQueue(all);
  }, []);

  /**
   * Единственная точка записи состояния household. Синхронно двигает dataRef —
   * иначе цепочка мутаций подряд (раскладка остатка по целям) читала бы устаревшее
   * значение: обычный useEffect обновляет ref только после рендера.
   */
  const commitData = useCallback((next: BootstrapData) => {
    dataRef.current = next;
    setData(next);
    void putSnapshot(next);
  }, []);

  /** Счётчик локально применённых мутаций — сторож гонки с ответом сервера. */
  const localEdits = useRef(0);

  /**
   * Дебаунс сверки с сервером: не чаще раза в 10 с, но и не реже — хвостовой
   * вызов гарантирован. Для случаев, когда данные уже применены локально и нужен
   * только сверочный запрос. Вызов идёт через ref: сам refresh объявлен ниже и
   * должен уметь просить пересверку.
   */
  const refreshFn = useRef<() => void>(() => {});
  const refreshScheduler = useMemo(
    () => createRefreshScheduler(() => refreshFn.current(), REFRESH_MIN_INTERVAL_MS),
    [],
  );

  // ---- bootstrap refresh ----
  const refresh = useCallback(async () => {
    if (inflight.current) return inflight.current;

    const editsAtStart = localEdits.current;
    const p = (async () => {
      let recheck = false;
      try {
        const fresh = await api.bootstrap();
        setError(null);
        setStatus('ready');
        setSync('online');
        setNeedsLogin(false);
        // 304: household не менялся. Не трогаем data и не переписываем снапшот —
        // новая ссылка пересчитала бы viewData и перерисовала дерево ни за чем,
        // а ещё затёрла бы локально применённые мутации. Статусы выше выставить
        // всё равно надо: сам факт ответа означает, что сеть и сессия живы.
        if (fresh.notModified) return;
        if (localEdits.current !== editsAtStart) {
          // Пока ответ был в пути, мы применили мутацию локально: снимок сделан
          // сервером ДО неё и стёр бы запись с экрана. Данные не берём — ETag
          // этого ответа тоже отстал от версии household, поэтому ближайшая
          // сверка приедет полной и уже с записью. Просим её из finally: пока мы
          // здесь, inflight ещё занят и повторный refresh был бы проглочен.
          recheck = true;
          return;
        }
        commitData(fresh.data);
      } catch (e) {
        if (e instanceof ApiError && e.code === 'unauthorized') {
          // сессия истекла: живём на снапшоте, просим re-login
          setNeedsLogin(true);
          if (dataRef.current) setStatus('ready');
          else {
            setStatus('error');
            setError('Сессия истекла — войдите заново');
          }
          return;
        }
        if (e instanceof ApiError && e.code === 'network') {
          setSync('offline');
          if (dataRef.current) {
            setStatus('ready');
          } else {
            setStatus('error');
            setError('Нет сети и нет локального кэша');
          }
          return;
        }
        const msg = e instanceof ApiError ? e.message : 'Не удалось загрузить данные';
        setError(msg);
        if (!dataRef.current) setStatus('error');
      } finally {
        inflight.current = null;
        if (recheck) refreshScheduler.request();
      }
    })();

    inflight.current = p;
    return p;
  }, [commitData, refreshScheduler]);

  useEffect(() => {
    refreshFn.current = () => void refresh();
  }, [refresh]);
  useEffect(() => () => refreshScheduler.cancel(), [refreshScheduler]);

  // ---- init: snapshot first, then network ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const snap = await getSnapshot();
      if (cancelled) return;
      if (snap) {
        dataRef.current = snap;
        setData(snap);
        setStatus('ready');
      }
      void refresh();
      void refreshQueue();
    })();
    return () => {
      cancelled = true;
    };
    // refresh намеренно НЕ в зависимостях — нужно ровно один init
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- drain wrapper ----
  const drainNow = useCallback(async () => {
    if (syncRef.current === 'syncing') return;
    if (!navigator.onLine) {
      setSync('offline');
      return;
    }
    setSync('syncing');
    const r = await drainQueue();

    // Слитые записи только что исчезли из очереди, а в data их ещё нет: без
    // этого шага операция, созданная офлайн, пропала бы с экрана в момент синка
    // и до ближайшей сверки. Очередь перечитываем ДО setState и ставим оба
    // состояния подряд, без await между ними: React 18 сводит их в один рендер,
    // и кадра «ни в очереди, ни в data» не возникает.
    const remaining = await listQueue();
    const prev = dataRef.current;
    const merged = prev && r.synced.length > 0 ? applyOptimistic(prev, r.synced) : null;
    setQueue(remaining);
    if (merged) {
      localEdits.current += 1;
      commitData(merged);
    }

    if (r.stoppedByAuth) {
      // очередь цела, но без сессии её не слить — гейтим периодику баннером
      setNeedsLogin(true);
      setSync('offline');
      return;
    }
    if (r.stoppedByNetwork) {
      setSync('offline');
      return;
    }
    setSync('online');
    if (r.synced.length > 0) {
      // данные уже применены локально — остаётся сверка, её и дебаунсим
      refreshScheduler.request();
    }
  }, [commitData, refreshScheduler]);

  const retryFailed = useCallback(async () => {
    await resetFailedAttempts();
    await refreshQueue();
    await drainNow();
  }, [drainNow, refreshQueue]);

  // ---- online/offline events ----
  useEffect(() => {
    function onOnline() {
      setSync('online');
      void drainNow();
    }
    function onOffline() {
      setSync('offline');
    }
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [drainNow]);

  // ---- periodic drain (только если есть pending и online; при needsLogin
  // не долбим 401 — восстановление через visibility-триггер после логина) ----
  useEffect(() => {
    const hasPending = queue.some((e) => e.status === 'pending');
    if (!hasPending || needsLogin) return;
    const t = setInterval(() => {
      if (navigator.onLine) void drainNow();
    }, DRAIN_INTERVAL_MS);
    return () => clearInterval(t);
  }, [queue, needsLogin, drainNow]);

  // ---- авто-refresh данных: при возврате на вкладку + плановый опрос пока открыто ----
  useEffect(() => {
    function onVisible() {
      if (!document.hidden && navigator.onLine) void refresh();
    }
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    // при needsLogin плановый опрос не нужен (401 каждые 3 минуты);
    // visibility/focus оставляем — возврат после логина само-восстановит
    const t = needsLogin
      ? null
      : setInterval(() => {
          if (!document.hidden && navigator.onLine) void refresh();
        }, POLL_INTERVAL_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      if (t) clearInterval(t);
    };
  }, [refresh, needsLogin]);

  // ---- mutation helper: try API, on network fail enqueue ----
  const tryOrQueue = useCallback(
    async (
      action: QueueAction,
      payload: Record<string, unknown>,
      callApi: () => Promise<unknown>,
    ): Promise<MutationResult & { result?: unknown }> => {
      try {
        const result = await callApi();
        setSync('online');
        // Раньше здесь был полный refresh на КАЖДУЮ мутацию (5 целей = 5
        // bootstrap'ов). Вместо него вливаем подтверждённую мутацию в data
        // локально: мутирующие эндпоинты возвращают только идентификатор
        // ({id}/{name}), а не сущность, поэтому применяем тот же payload тем же
        // кодом, что и очередь. Без этого запись не была бы видна до ближайшего
        // опроса: при успехе она в очередь не попадает вовсе, а в data её нет.
        const prev = dataRef.current;
        if (prev) {
          localEdits.current += 1;
          commitData(applyConfirmed(prev, action, payload));
        }
        return { synced: true, result };
      } catch (e) {
        if (
          e instanceof ApiError &&
          (e.code === 'network' || e.code === 'internal' || e.code === 'unauthorized')
        ) {
          // unauthorized: мутация не должна потеряться — в очередь, баннер re-login
          if (e.code === 'unauthorized') setNeedsLogin(true);
          await enqueue(action, payload);
          await refreshQueue();
          setSync('offline');
          return { synced: false };
        }
        throw e;
      }
    },
    [commitData, refreshQueue],
  );

  const addTx = useCallback<ContextValue['addTx']>(
    async (input) => {
      const id = newClientId();
      const full: AddTxInput = { ...input, id };
      const payload = full as unknown as Record<string, unknown>;
      const r = await tryOrQueue('addTx', payload, () => api.addTx(full));
      return { ...r, id };
    },
    [tryOrQueue],
  );

  const updateTx = useCallback<ContextValue['updateTx']>(
    async (input) =>
      tryOrQueue(
        'updateTx',
        input as unknown as Record<string, unknown>,
        () => api.updateTx(input),
      ),
    [tryOrQueue],
  );

  const deleteTx = useCallback<ContextValue['deleteTx']>(
    async (id) => tryOrQueue('deleteTx', { id }, () => api.deleteTx(id)),
    [tryOrQueue],
  );

  const updateGoal = useCallback<ContextValue['updateGoal']>(
    async (input) =>
      tryOrQueue(
        'updateGoal',
        input as unknown as Record<string, unknown>,
        () => api.updateGoal(input),
      ),
    [tryOrQueue],
  );

  const updateCategory = useCallback<ContextValue['updateCategory']>(
    async (input) =>
      tryOrQueue(
        'updateCategory',
        input as unknown as Record<string, unknown>,
        () => api.updateCategory(input),
      ),
    [tryOrQueue],
  );

  const addGameEvent = useCallback<ContextValue['addGameEvent']>(
    async (input) => {
      const r = await tryOrQueue(
        'addGameEvent',
        input as unknown as Record<string, unknown>,
        () => api.addGameEvent(input),
      );
      const existing = (r.result as { existing?: boolean } | undefined)?.existing;
      return { synced: r.synced, existing };
    },
    [tryOrQueue],
  );

  const putReserve = useCallback<ContextValue['putReserve']>(
    async (input) =>
      tryOrQueue(
        'putReserve',
        input as unknown as Record<string, unknown>,
        () => api.putReserve(input),
      ),
    [tryOrQueue],
  );

  const sweepReserve = useCallback<ContextValue['sweepReserve']>(
    async (input) =>
      tryOrQueue(
        'sweepReserve',
        input as unknown as Record<string, unknown>,
        () => api.sweepReserve(input),
      ),
    [tryOrQueue],
  );

  // ---- derived: viewData (data + optimistic queue replay) ----
  const viewData = useMemo(() => {
    if (!data) return null;
    return applyOptimistic(data, queue);
  }, [data, queue]);

  const failedCount = useMemo(() => queue.filter((e) => e.status === 'failed').length, [queue]);

  const value = useMemo<ContextValue>(
    () => ({
      status,
      data,
      viewData,
      error,
      sync,
      queue,
      failedCount,
      needsLogin,
      refresh,
      addTx,
      updateTx,
      deleteTx,
      updateGoal,
      updateCategory,
      addGameEvent,
      putReserve,
      sweepReserve,
      drainNow,
      retryFailed,
    }),
    [
      status,
      data,
      viewData,
      error,
      sync,
      queue,
      failedCount,
      needsLogin,
      refresh,
      addTx,
      updateTx,
      deleteTx,
      updateGoal,
      updateCategory,
      addGameEvent,
      putReserve,
      sweepReserve,
      drainNow,
      retryFailed,
    ],
  );

  return <BudgetCtx.Provider value={value}>{children}</BudgetCtx.Provider>;
}

export function useBudget(): ContextValue {
  const v = useContext(BudgetCtx);
  if (!v) throw new Error('useBudget must be used inside <BudgetProvider>');
  return v;
}

// ============================================================================
// Optimistic apply
// ============================================================================

/**
 * Влить в `data` мутацию, подтверждённую сервером.
 *
 * Мутирующие эндпоинты отвечают идентификатором (`{id}`, `{name}`), а не
 * сущностью, — вливать в состояние нечего, кроме того же payload, что ушёл на
 * сервер. Поэтому никакой отдельной логики: это ровно `applyOptimistic` с одной
 * синтетической записью очереди, и правила применения гарантированно совпадают
 * с офлайн-путём.
 *
 * Чего этот путь НЕ делает: серверные агрегаты (`aggregates`) и переименование
 * категории. Агрегаты пересчитываются на ближайшем плановом опросе, а
 * переименование редкое и его экраны просят `refresh()` явно.
 */
export function applyConfirmed(
  data: BootstrapData,
  action: QueueAction,
  payload: Record<string, unknown>,
  createdAt: number = Date.now(),
): BootstrapData {
  return applyOptimistic(data, [
    { id: `confirmed-${createdAt}`, action, payload, createdAt, attempts: 0, status: 'pending' },
  ]);
}

export function applyOptimistic(data: BootstrapData, queue: QueueEntry[]): BootstrapData {
  const deleteIds = new Set<string>();
  const goalPatches = new Map<string, Partial<Goal>>();
  const categoryPatches = new Map<string, Partial<Category>>();
  const opPatches = new Map<string, Partial<Operation>>();
  const newOps: Operation[] = [];
  const newEvents: GameEvent[] = [];
  // судьба резервов: ключ `${month}|${category}` → state|null (null = снять)
  const reservePatches = new Map<string, ReserveStateRow['state'] | null>();
  // sweep: дельты к goal.have (поверх абсолютных goalPatches)
  const sweepDeltas = new Map<string, number>();

  for (const entry of queue) {
    if (entry.status !== 'pending') continue;
    switch (entry.action) {
      case 'deleteTx': {
        const id = (entry.payload as { id?: string }).id;
        if (id) deleteIds.add(id);
        break;
      }
      case 'addTx': {
        const p = entry.payload as Partial<AddTxInput>;
        const cat = data.categories.find((c) => c.name === p.category);
        const synthId = p.id || entry.id;
        // Запись уже в data (мутация подтверждена, а очередь ещё не перечитана
        // из IDB) — второй копии на экране быть не должно.
        if (data.operations.some((o) => o.id === synthId)) break;
        newOps.push({
          id: synthId,
          date: p.date ?? '',
          category: p.category ?? '',
          type: cat?.type ?? '',
          amount: typeof p.amount === 'number' ? p.amount : 0,
          who: p.who ?? '',
          comment: p.comment ?? '',
          created_at: new Date(entry.createdAt).toISOString(),
        });
        break;
      }
      case 'updateTx': {
        const p = entry.payload as Partial<UpdateTxInput>;
        if (!p.id) break;
        const existing = opPatches.get(p.id) ?? {};
        const { is_one_off, ...rest } = p;
        opPatches.set(p.id, {
          ...existing,
          ...rest,
          ...(is_one_off === undefined ? {} : { is_one_off: is_one_off ? 1 : 0 }),
        });
        break;
      }
      case 'updateGoal': {
        const p = entry.payload as unknown as UpdateGoalInput;
        if (!p.name) break;
        const existing = goalPatches.get(p.name) ?? {};
        const { is_shared, ...rest } = p;
        const normalized: Partial<Goal> = {
          ...existing,
          ...rest,
          ...(is_shared === undefined ? {} : { is_shared: is_shared ? 1 : 0 }),
        };
        goalPatches.set(p.name, normalized);
        break;
      }
      case 'addGameEvent': {
        // оптимистично: сундук/сезон видны закрытыми ещё до синка
        const p = entry.payload as unknown as AddGameEventInput;
        if (!p.id || data.game_events?.some((e) => e.id === p.id)) break;
        newEvents.push({
          id: p.id,
          type: p.type,
          month: p.month,
          week: p.week ?? '',
          coins: p.coins ?? 0,
          payload: p.payload ?? '',
          created_at: new Date(entry.createdAt).toISOString(),
        });
        break;
      }
      case 'putReserve': {
        const p = entry.payload as unknown as PutReserveInput;
        if (!p.month || !p.category) break;
        reservePatches.set(`${p.month}|${p.category}`, p.state);
        break;
      }
      case 'sweepReserve': {
        const p = entry.payload as unknown as SweepReserveInput;
        if (!p.month || !p.category || !p.goal) break;
        reservePatches.set(`${p.month}|${p.category}`, 'swept');
        sweepDeltas.set(p.goal, (sweepDeltas.get(p.goal) || 0) + (p.amount || 0));
        break;
      }
      case 'updateCategory': {
        const p = entry.payload as unknown as UpdateCategoryInput;
        if (!p.name) break;
        const existing = categoryPatches.get(p.name) ?? {};
        // newName — поле запроса, а не колонка: оптимистично переименование не
        // показываем (имя категории — внешний ключ операций и резервов), но и
        // мусорить служебным полем в data и снапшоте IDB не надо.
        const { newName: _newName, ...rest } = p;
        const normalized: Partial<Category> = {
          ...existing,
          ...rest,
          is_hot: p.is_hot === undefined ? existing.is_hot : (p.is_hot ? 1 : 0),
          is_daily: p.is_daily === undefined ? existing.is_daily : (p.is_daily ? 1 : 0),
        };
        categoryPatches.set(p.name, normalized);
        break;
      }
    }
  }

  const goals = data.goals.map((g) => {
    const patch = goalPatches.get(g.name);
    const delta = sweepDeltas.get(g.name) || 0;
    if (!patch && !delta) return g;
    const merged: Goal = { ...g, ...(patch ?? {}) } as Goal;
    const need = Number(merged.need ?? 0);
    const have = Number(merged.have ?? 0) + delta;
    merged.have = have;
    merged.remaining = need - have;
    merged.progress = need > 0 ? have / need : 0;
    return merged;
  });

  let reserveState = data.reserve_state ?? [];
  if (reservePatches.size > 0) {
    const byKey = new Map<string, ReserveStateRow>(
      reserveState.map((r) => [`${r.month}|${r.category}`, r]),
    );
    for (const [key, state] of reservePatches) {
      if (state === null) byKey.delete(key);
      else {
        const [month, category] = key.split('|');
        byKey.set(key, { month, category, state });
      }
    }
    reserveState = [...byKey.values()];
  }

  const categories = data.categories.map((c) => {
    const patch = categoryPatches.get(c.name);
    return patch ? ({ ...c, ...patch } as Category) : c;
  });

  const operations: Operation[] = [
    ...data.operations
      .filter((o) => !deleteIds.has(o.id))
      .map((o) => {
        const patch = opPatches.get(o.id);
        if (!patch) return o;
        const merged = { ...o, ...patch } as Operation;
        // Если категория поменялась — обновим type из категории
        if (patch.category) {
          const cat = data.categories.find((c) => c.name === patch.category);
          if (cat) merged.type = cat.type;
        }
        return merged;
      }),
    ...newOps,
  ];

  return {
    settings: data.settings,
    categories,
    goals,
    operations,
    // старые snapshot'ы из IDB могут не иметь game_events
    game_events: [...(data.game_events ?? []), ...newEvents],
    pending_count: data.pending_count,
    reserve_state: reserveState,
    // Агрегаты считает сервер по всей истории — очередь их не меняет, но
    // перенести обязаны: экраны читают viewData, и без этого агрегаты
    // до UI не доезжают вовсе.
    aggregates: data.aggregates,
  };
}
